/*!
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2022 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */

import React, { FC, useEffect, useState, useSyncExternalStore } from "react";
import { Alert } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useInRouterContext, useLocation } from "react-router-dom";
import {
  clearRequestErrors,
  dismissRequestError,
  isRequestErrorVisible,
  subscribeRequestError,
} from "../../utils/networkError";

// Layouts can nest (e.g. a routes layout `.content` around a page using
// ContentWrapperComponent), so only one mounted instance renders the alert:
// the first to mount, i.e. the layout one, next to its ApiError.
const mountedIds: number[] = [];
const instanceListeners = new Set<() => void>();
let nextId = 0;

const subscribeInstances = (listener: () => void) => {
  instanceListeners.add(listener);
  return () => {
    instanceListeners.delete(listener);
  };
};
const getActiveId = () => mountedIds[0] ?? -1;
const notifyInstances = () => instanceListeners.forEach((l) => l());

// Shared across instances: a page's own instance mounts with the new path while
// the old page's one unmounts, so no single instance sees the change.
let lastPathname: string | undefined;

// Leaving a page dismisses the alert; the next failed request shows it again.
const DismissOnRouteChange: FC = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    if (lastPathname !== undefined && lastPathname !== pathname)
      dismissRequestError();
    lastPathname = pathname;
  }, [pathname]);
  return null;
};

/**
 * "Unable to connect to API, please try again." styled like ErrorAlert, shown
 * at the top of the page content when a request fails without a response.
 * Place it first inside `<div className="content">`; it hides itself once the
 * failing backend responds again, the browser comes back online, or the user
 * navigates to another page.
 */
export const NetworkRequestErrorAlert: FC = () => {
  const { t } = useTranslation("common");
  const [id] = useState(() => {
    nextId += 1;
    return nextId;
  });
  const visible = useSyncExternalStore(
    subscribeRequestError,
    isRequestErrorVisible,
    isRequestErrorVisible
  );
  const activeId = useSyncExternalStore(
    subscribeInstances,
    getActiveId,
    getActiveId
  );
  const inRouter = useInRouterContext();

  useEffect(() => {
    mountedIds.push(id);
    notifyInstances();
    window.addEventListener("online", clearRequestErrors);
    return () => {
      mountedIds.splice(mountedIds.indexOf(id), 1);
      notifyInstances();
      window.removeEventListener("online", clearRequestErrors);
    };
  }, [id]);

  return (
    <>
      {inRouter && <DismissOnRouteChange />}
      {visible && activeId === id && (
        <Alert
          variant="danger"
          dismissible
          onClose={dismissRequestError}
          className="alert alert-styled-left"
        >
          <div>{t("server_error_code.502")}</div>
        </Alert>
      )}
    </>
  );
};

export default NetworkRequestErrorAlert;
